import { Application } from 'pixi.js';
import {
  TICK_SECONDS,
  buildNodeIndex,
  className,
  createWorld,
  edgeNameOf,
  OUT_OF_STATE,
  loadCounty,
  makeProjection,
  unproject,
  formatClock,
  routeRemaining,
  type Obstruction,
  nearestNode,
  nearestRoadNode,
  tickWorld,
  type County,
  type SpatialIndex,
  type Vehicle,
  type World,
  ARRIVAL_METERS,
  DAY_SECONDS,
  REFUEL_METERS,
  RADAR_PRICE,
  type Interference,
  type InspectReplyMessage,
  type RideAlongStateMessage,
} from '@hotlist/shared';
import { AlertPanel } from './alerts.ts';
import { AuditPanel } from './audit.ts';
import { CameraLayer } from './cameralayer.ts';
import { DayNight } from './daynight.ts';
import { HeatmapLayer } from './heatmap.ts';
import { JourneyLayer } from './journey.ts';
import { ActionBar, type ActionName } from './actions.ts';
import { ConsolePanel } from './console.ts';
import { DispatchPanel } from './dispatch.ts';
import { GaragePanel } from './garage.ts';
import { WorkPanel } from './work.ts';
import { CitationsPanel } from './citations.ts';
import { AdminPanel } from './adminpanel.ts';
import { CameraMenu } from './cameramenu.ts';
import { CamerasApp } from './camerasapp.ts';
import { ScannerApp } from './scanner.ts';
import { SatelliteLayer } from './satellite.ts';
import { SettingsPanel } from './settings.ts';
import { initGuide } from './guide.ts';
import { Sound } from './sound.ts';
import { YourWeekPanel } from './yourweek.ts';
import { StandingPanel } from './standing.ts';
import { RouteLayer } from './routes.ts';
import { Keyring } from './keyring.ts';
import { LandingScene } from './landing.ts';
import { MarkerLayer } from './markers.ts';
import { CommunityHeld } from './community.ts';
import { Coverage, KnownCameras } from './knowncameras.ts';
import { Hud } from './hud.ts';
import { NetClient } from './net.ts';
import { QueryPanel } from './query.ts';
import { RoadLayer } from './roads.ts';
import type { HudModel, Renderable, VehicleInfo } from './state.ts';
import { BACKGROUND } from './theme.ts';
import { VehicleLayer } from './vehicles.ts';
import { Viewport } from './viewport.ts';

/**
 * The client, in two modes.
 *
 * **Live** is M1: connected to the authoritative server, sending intents and
 * receiving snapshots. Click a road and your vehicle drives there, because the
 * server routed it and said yes.
 *
 * **Solo** is M0, kept because it is still the fastest way to look at a county
 * and because it runs the same simulation module the server runs. Add `?solo`
 * to the URL.
 *
 * What is worth watching in live mode is what the other cars are. The server
 * sends a position and a heading and never a plate, so clicking one tells you
 * where it is and nothing about whose it is. That gap is the game.
 */

const SIM_SPEEDS = [1, 4, 12];

const boot = document.getElementById('boot') as HTMLElement;
const bootLine = document.getElementById('boot-line') as HTMLElement;

void start();

async function start(): Promise<void> {
  try {
    bootLine.textContent = 'loading county';
    const county = await fetchCounty();
    bootLine.textContent =
      `${county.name}: ${county.nodeCount.toLocaleString()} junctions, ` +
      `${county.edgeCount.toLocaleString()} roads`;
    await run(county);
  } catch (error) {
    bootLine.classList.add('error');
    bootLine.textContent = error instanceof Error ? error.message : String(error);
  }
}

/**
 * Which county to load. `?county=sauk` picks data/counties/sauk.json, so a
 * second bake can be looked at without rebaking over the first one.
 */
function countyUrl(): string {
  const name = new URLSearchParams(location.search).get('county') ?? 'county';
  const safe = name.replace(/[^a-z0-9_-]/gi, '');
  return `/counties/${safe || 'county'}.json`;
}

/**
 * Where the authoritative server is.
 *
 * Two shapes, because development and deployment want different ones, and the
 * bundler already knows for certain which of the two this build is.
 *
 * In development the server is a second process on a second port and the client
 * talks straight to it, which is what `VITE_HOTLIST_SERVER_PORT` is for.
 *
 * A build is behind a reverse proxy, where that is wrong: a browser on
 * `https://host` cannot open `wss://host:5291` without a second certificate on
 * that port, and putting one there means a second name, a second renewal, and a
 * port that has to be open to the world. So the socket rides the same origin as
 * the page -- one certificate, one port, and the game server never faces the
 * internet at all.
 *
 * `/ws` is written here rather than passed in as a build variable on purpose.
 * It was a variable, and a shell rewrote the value on its way to the build:
 * MSYS path conversion turned `/ws` into `C:/Program Files/Git/ws`, which
 * compiled cleanly, deployed cleanly, and produced a client that could not
 * reach its own server. A constant cannot be corrupted in transit, and the
 * proxy path is a deploy detail this file and the nginx site have to agree on
 * anyway.
 */
const SOCKET_PATH = '/ws';

function serverUrl(): string {
  const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
  if (!import.meta.env.DEV) return `${scheme}://${location.host}${SOCKET_PATH}`;
  const port = import.meta.env.VITE_HOTLIST_SERVER_PORT ?? '5291';
  return `${scheme}://${location.hostname}:${port}`;
}

async function fetchCounty(): Promise<County> {
  const url = countyUrl();
  const response = await fetch(url);
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    if (body?.error === 'no county baked') {
      throw new Error(
        'No county has been baked yet.\n\nRun this from the repo root:\n\n    pnpm bake\n\nThen reload.',
      );
    }
    throw new Error(`${url} returned ${response.status}`);
  }
  try {
    return loadCounty(await response.json());
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new Error(
      [
        detail,
        '',
        `The county at ${url} was baked by an older build.`,
        'Re-bake it from the repo root:',
        '',
        '    pnpm bake',
        '',
        'Then reload.',
      ].join('\n'),
    );
  }
}

async function run(county: County): Promise<void> {
  const solo = new URLSearchParams(location.search).has('solo');
  const projection = makeProjection();

  const app = new Application();
  await app.init({
    background: BACKGROUND,
    resizeTo: window,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(2, window.devicePixelRatio || 1),
    preference: 'webgl',
  });
  (document.getElementById('stage') as HTMLElement).append(app.canvas);

  const viewport = new Viewport(projection);
  viewport.resize(app.screen.width, app.screen.height);

  const roads = new RoadLayer(county, projection);
  const vehicles = new VehicleLayer(app.renderer, projection);
  // What this player has found by driving. Loaded from the last session,
  // because a pole does not move and a map that reset every time would make
  // discovery busywork instead of investment.
  const known = new KnownCameras();
  // What everybody else says is out there, which is a different and weaker
  // claim than what you found yourself, and is drawn as one.
  const community = new CommunityHeld();
  // What the agency operates, while on shift. Never persisted, never merged.
  const coverage = new Coverage();
  const cameraLayer = new CameraLayer(app.renderer, projection, known, community);
  cameraLayer.setCoverage(coverage);
  // Where you are going, and -- when avoidance is on -- what it is costing you.
  const routeLayer = new RouteLayer(county, projection);
  // Where the job in hand actually goes, drawn as the places themselves.
  const markers = new MarkerLayer(app.renderer, projection, county);
  // Under everything: the record is the thing being played and the photograph
  // is context. See satellite.ts for why it is off until somebody asks.
  const satellite = new SatelliteLayer(app.renderer, county, projection);
  // The county's clock made visible: a day/night wash over the whole scene.
  const dayNight = new DayNight(app.renderer);
  // A plate's traced journey -- an operator overlay, above the wash so it reads
  // at any hour.
  const journeyLayer = new JourneyLayer(app.renderer, projection, county, DAY_SECONDS);
  // The traffic heatmap: where the network reads, as a density wash.
  const heatmapLayer = new HeatmapLayer(app.renderer, projection);

  app.stage.addChild(
    satellite.container,
    roads.container,
    routeLayer.container,
    cameraLayer.container,
    vehicles.container,
    markers.container,
    roads.labelContainer,
    // The light falls on everything below it uniformly.
    dayNight.container,
    // Above the light: operator overlays, tools rather than part of the scene.
    heatmapLayer.container,
    journeyLayer.container,
  );

  // The heatmap's "when": 24 bars of reads by hour of the county day, shown over
  // the map with the density wash and cleared when the console comes back up.
  const showHeatHours = (hours: readonly number[]): void => {
    const bars = document.getElementById('heat-hours-bars');
    const panel = document.getElementById('heat-hours');
    if (!bars || !panel) return;
    const max = Math.max(1, ...hours);
    bars.replaceChildren(
      ...hours.map((h, i) => {
        const bar = document.createElement('div');
        bar.className = 'hh-bar';
        bar.style.height = `${Math.max(2, Math.round((h / max) * 44))}px`;
        bar.title = `${String(i).padStart(2, '0')}:00 - ${h} reads`;
        return bar;
      }),
    );
    panel.hidden = false;
  };
  const hideHeatHours = (): void => {
    const panel = document.getElementById('heat-hours');
    if (panel) panel.hidden = true;
  };
  // The heatmap is a plain on/off toggle: it stays on across opening the console
  // and switching tabs, and the same button turns it back off. The two map
  // overlays are exclusive -- turning one on clears the other -- so the map never
  // has to carry both at once.
  const toggleHeatmap = (): void => {
    if (heatmapLayer.active) {
      heatmapLayer.clear();
      hideHeatHours();
    } else {
      journeyLayer.clear();
      net?.heatmap();
    }
  };
  viewport.fit(county);

  const hud = new Hud(county);
  const nodeIndex = buildNodeIndex(county, 500);

  // The enforcement side. Asked for with ?enforcement, granted by the server,
  // and hidden entirely until it is.
  const params = new URLSearchParams(location.search);
  // ?admin gives the reserved plate and the network with it. The server checks
  // it against a key it holds and decides; the client only asks.
  const adminKey = params.has('admin') ? (params.get('admin') ?? '') : undefined;
  const wantsNetwork = params.has('enforcement') || adminKey !== undefined;
  const query = solo
    ? null
    : new QueryPanel(county, {
        search: (by, term, reason) => net?.search(by, term, reason),
        // The officer's own tools, the same ones the record shows police using:
        // trace a searched plate's reads on the map, and the traffic heatmap for
        // the cameras they can see.
        trace: (points) => {
          heatmapLayer.clear();
          hideHeatHours();
          journeyLayer.setJourney(points, net?.router);
          consolePanel?.close();
        },
        heatmap: () => toggleHeatmap(),
      });
  // The audit panel. Present from the first frame of enforcement, opened only
  // if the player decides to; see audit.ts for why that is the whole point.
  const consolePanel = solo
    ? null
    : new ConsolePanel((want) => net?.side(want));
  /**
   * Opening an app fetches what it shows.
   *
   * The work board turns over on a timer at the server, so whatever was last
   * pushed is stale by the time somebody goes looking. Asking on open is one
   * message when a screen is opened, which is nothing, and it is the difference
   * between a board with work on it and an empty one.
   */
  consolePanel?.whenOpened((name) => {
    if (name === 'work' || name === 'garage') net?.job();
    if (name === 'standing') net?.standing();
  });
  // What you can do right now. The whole of the phone interface, and a better
  // way to find the actions on any device than nine memorised keys.
  const actions = new ActionBar((name) => runAction(name));
  const citations = new CitationsPanel();
  const cameras = solo
    ? null
    : new CamerasApp({
        toggleAvoid: () => {
          toggleAvoid();
          paintCameras();
        },
        file: () => fileSightings(),
        toggleCommunity: () => toggleCommunity(),
      });

  /**
   * The landing screen, for a browser holding no keys.
   *
   * The first thing a stranger used to see was an unexplained county and a
   * modal that looked like a DMV form asking for their real plate. So nobody
   * typed one and plenty closed the tab. The card says what this is and that
   * the plates are fiction; Drive dismisses it and the car is already waiting,
   * because the server hands one over on connect whether or not anybody reads
   * anything.
   */
  /**
   * The front door, and who opens it.
   *
   * `atTheDoor` is the load-bearing bit: when true, the connection is held and
   * no car exists anywhere -- the game literally has not been joined. Drive
   * starts the connection and asks the device for its unlock once the first
   * welcome lands; the other door signs in and the server seats you in your
   * own car before the page ever shows through.
   */
  const atTheDoor = !solo && adminKey === undefined && !new Keyring().current();
  let showLanding: (() => void) | null = null;
  let landingScene: LandingScene | null = null;
  /**
   * The front door is a gate, not a curtain.
   *
   * You cannot reach the game without a passkey. The overlay stays up through
   * the whole ceremony and only lifts on success -- a completed sign-in or a
   * completed save. Cancel either and you are still at the door, because a
   * player with no saved game is a browser that would refresh, spawn a car,
   * abandon it, and do it again forever. `door` is that gate's state.
   */
  let door: 'closed' | 'signin' | 'enroll' | 'blocked' = 'closed';
  /** The side chosen at the door, applied the instant the gate lifts. */
  let doorSide: 'enforcement' | null = null;
  const doorButtons = [
    document.getElementById('landing-drive'),
    document.getElementById('landing-authority'),
  ];
  const setDoorBusy = (busy: boolean): void => {
    for (const button of doorButtons) {
      if (button) (button as HTMLButtonElement).disabled = busy;
    }
  };
  {
    const landing = document.getElementById('landing');
    const status = document.getElementById('landing-status');
    if (landing && !solo && adminKey === undefined) {
      /**
       * The county behind the page. Started only while the door is open and
       * stopped the moment it closes, because the game wants the GPU and a
       * hidden canvas animating a county nobody can see is pure heat.
       */
      const canvas = document.getElementById('landing-county') as HTMLCanvasElement | null;
      const scene = canvas
        ? new LandingScene(county, canvas, document.getElementById('landing-wire'))
        : null;
      landingScene = scene;
      showLanding = () => {
        // Reopening the door is starting over: the gate is idle and the two
        // buttons are live. Without this reset a sign-out returned to a door
        // whose buttons were still disabled from the ceremony that let the
        // player in, so nothing was clickable until a full refresh.
        door = 'closed';
        doorSide = null;
        setDoorBusy(false);
        const status = document.getElementById('landing-status');
        if (status) status.hidden = true;
        landing.hidden = false;
        scene?.start();
      };
      // Shown for everyone but the administrator, matching the inline gate. A
      // valid auto-resume lifts it; a keyless or dead-key browser keeps it for
      // the ceremony.
      landing.hidden = false;
      scene?.start();

      // The county's pulse, so the door shows the place running -- and the
      // towns reads may honestly appear in.
      void fetch('/pulse')
        .then((r) => (r.ok ? r.json() : null))
        .then((pulse: Record<string, unknown> | null) => {
          if (!pulse) return;
          const strip = document.getElementById('landing-pulse');
          if (strip) strip.hidden = false;
          for (const [key, id] of [
            ['cameras', 'pulse-cameras'],
            ['roads', 'pulse-roads'],
            ['vehicles', 'pulse-vehicles'],
            ['driving', 'pulse-driving'],
          ] as const) {
            const cell = document.getElementById(id);
            if (cell && typeof pulse[key] === 'number') {
              cell.textContent = (pulse[key] as number).toLocaleString('en-US');
            }
          }
        })
        .catch(() => {});

      /**
       * The two doors are the two sides of the game, and each one is a lock.
       *
       * A click starts the ceremony behind the still-standing overlay: sign in
       * if this device holds a passkey, create one if it does not. The game is
       * on the far side of a completed passkey and nowhere nearer -- cancel and
       * the door is still shut, with a line saying why and the buttons live to
       * try again.
       */
      const openDoor = (side: 'civilian' | 'enforcement'): void => {
        if (door === 'signin' || door === 'enroll') return;
        doorSide = side === 'enforcement' ? 'enforcement' : null;
        door = 'signin';
        setDoorBusy(true);
        doorSaid('Checking this device...');
        net?.start();
        // Already connected -- a prior door click, or a dead key reopened the
        // gate mid-session. The ceremony runs now, on this click's gesture.
        if (net?.selfVehicle) net.passkey('open');
      };
      doorButtons[0]?.addEventListener('click', () => openDoor('civilian'));
      doorButtons[1]?.addEventListener('click', () => openDoor('enforcement'));
    }
  }

  /** The gate lifts: apply the chosen side and hand the world over. */
  // The Field Manual on the landing. Wired once; no-ops where the landing is not
  // present (solo, admin).
  initGuide();

  const enterGame = (): void => {
    door = 'closed';
    const landing = document.getElementById('landing');
    if (landing) landing.hidden = true;
    const status = document.getElementById('landing-status');
    if (status) status.hidden = true;
    landingScene?.stop();
    // The hand-driving control comes up with the game, so a phone player has a
    // way to steer from the first second.
    const compass = document.getElementById('compass');
    if (compass) compass.hidden = false;
    if (doorSide) net?.side(doorSide);
    doorSide = null;
  };

  // The compass comes up with the game for everyone who is not stopped at the
  // door -- admin and solo skip enterGame, and a returning player auto-resumes,
  // so unhiding it only there would leave all three without a way to steer.
  if (!atTheDoor) {
    const compassEl = document.getElementById('compass');
    if (compassEl) compassEl.hidden = false;
  }

  const doorSaid = (said: string): void => {
    const status = document.getElementById('landing-status');
    if (status) {
      status.hidden = false;
      status.textContent = said;
    }
  };

  const scanner = solo
    ? null
    : new ScannerApp({
        buy: () => net?.buy('scanner'),
        // A scanner names a town, so the map goes to the town and no closer.
        look: (town) => {
          const place = county.places.find((p) => p.name === town);
          if (!place) return;
          lookAt(place.x, place.y);
          consolePanel?.close();
        },
      });

  const cameraMenu = new CameraMenu({
    cover: (means) => interfereWith('obstruct', means),
    heavier: (tier) => interfereWith(tier),
  });

  const yourWeek = new YourWeekPanel(() => net?.week());
  const standingPanel = solo ? null : new StandingPanel();
  // On, and not a setting. A siren you can hear from three streets away is a
  // thing the world does, the same as a camera being on a pole; putting it
  // behind a switch would make it a preference, which it is not. The only
  // reason it does not start immediately is that browsers will not make noise
  // before a gesture, and the first click does that on its own.
  const sound = new Sound();
  sound.setOn(true);
  sound.listenForGesture();
  const settings = new SettingsPanel([
    {
      name: 'satellite',
      title: 'Aerial imagery',
      detail: 'Satellite photography under the map.',
      on: () => satellite.on,
      available: () => satellite.possible,
      unavailable: 'This county has no georeferencing, so there is nothing to line imagery up with.',
      flip: () => {
        roads.setUnderlay(satellite.toggle());
      },
    },
  ]);
  // Only built when this client holds a key. Nothing about administration is
  // reachable, or even present in the interface, without one.
  const admin =
    adminKey === undefined
      ? null
      : new AdminPanel({
          refresh: () => net?.admin(),
          reset: (what) => net?.reset(what),
          oversight: (plate) => net?.oversight(plate),
          // Draw the plate's journey on the map and step the dashboard aside so
          // it can be seen. Opening the console again clears it.
          trace: (points) => {
          heatmapLayer.clear();
          hideHeatHours();
          journeyLayer.setJourney(points, net?.router);
            consolePanel?.close();
          },
          heatmap: () => toggleHeatmap(),
          rideAlong: (id) => {
            input.selectedId = id;
            input.rideAlongId = id;
            input.following = true;
            net?.rideAlong(id);
          },
          stopRideAlong: () => {
            input.rideAlongId = null;
            input.following = false;
          },
          viewCamera: (id) => net?.cameraDetail(id),
          join: (id) => startJoin(id),
        });
  // The map overlays -- a traced journey, the traffic heatmap -- are meant to be
  // read with the console closed; opening it puts the dashboard back over the
  // map, so they are cleared.
  // Opening the console clears a one-shot plate trace (it was a "look here"), but
  // never the heatmap -- that is a toggle the operator turns off themselves.
  consolePanel?.whenOpened(() => journeyLayer.clear());
  const audit = solo ? null : new AuditPanel(() => net?.audit());
  const dispatch = solo
    ? null
    : new DispatchPanel(
        () => net?.dispatch(),
        (camera) => {
          // Drive to the nearest junction to it, then say you looked. The
          // server checks you were actually there.
          const target = pendingDispatch.get(camera);
          if (target) {
            const node = nearestNode(county, nodeIndex, target.x, target.y);
            if (node >= 0) driveTo(node);
          }
          net?.dispatch(camera);
        },
        () => net?.buy('radar'),
      );
  /** Where each open dispatch is, so "drive there" has somewhere to go. */
  const pendingDispatch = new Map<string, { x: number; y: number }>();
  const garage = solo
    ? null
    : new GaragePanel({
        switchTo: (plate) => net?.switchTo(plate),
        buy: (what) => net?.buy(what),
        // Out through the front door: keys forgotten here, socket down,
        // landing back up. The passkey stays in the device, so one touch on
        // "I have a car here" undoes all of it.
        signOut: () => {
          net?.signOut();
          consolePanel?.close();
          showLanding?.();
        },
        plateCheck: (plate, state) => net?.plateCheck(plate, state),
        plateBuy: (plate, state) => net?.plateBuy(plate, state),
      });
  const work = solo
    ? null
    : new WorkPanel({
        take: (id) => net?.job('take', id),
        arrive: () => net?.job('arrive'),
        abandon: () => net?.job('abandon'),
        buy: (what) => net?.buy(what),
        route: (node) => driveTo(node),
      });
  /**
   * Pan the map somewhere and mark the spot.
   *
   * What every "where is that" click shares: alerts pan to the camera that
   * fired, scanner calls pan to the town the radio named. The pulse matters as
   * much as the pan -- a pan alone leaves the eye searching the same screen it
   * was already searching.
   */
  const lookAt = (x: number, y: number): void => {
    input.following = false;
    viewport.centerOnWorld(x, y);
    markers.lookAt(x, y);
  };

  const alerts = solo
    ? null
    : new AlertPanel(county, {
        add: (plate, reason, note) => net?.hotlist('add', plate, reason, note),
        clear: (plate) => net?.hotlist('clear', plate),
        look: (node) => {
          if (node < 0 || node >= county.nodeCount) return;
          lookAt(county.nodeX[node] as number, county.nodeY[node] as number);
          consolePanel?.close();
        },
      });

  let frame: Renderable[] = [];
  /**
   * Whether this player is routing around what they have found.
   *
   * Off by default, because taking the long way is a decision with a cost and
   * the game should not make it for anybody. Toggling it re-sends the whole
   * known set, which is the only thing the server ever learns about what this
   * player knows.
   */
  let avoiding = false;
  /** Whether the administrator is currently using the speed allowance. */
  let fast = false;
  /** Whether the shared network is switched on for this officer. */
  let networked = false;
  /** What is in the car, and what the current job is, for the action bar. */
  let money = 0;
  let hasJob = false;
  let jobDrop = -1;
  let jobPays = 0;
  let jobDropName = '';
  /** Whether the leg in hand is the pickup rather than the delivery. */
  let jobCollecting = false;
  /** The county's day, for turning world seconds into hours a player reads. */
  let worldDaySeconds = DAY_SECONDS;
  /** The tank's size and the nearest pump, both of which change slowly. */
  let tank = 0;
  let nearestFuel: { name: string; node: number; away: number } | null = null;

  /**
   * Every filling station in the county, held once.
   *
   * The nearest one used to arrive on the job-board message, which is sent when
   * you take a job or buy something and **not** while you drive. So `away` was
   * however far the pump had been the last time a board happened to go out, and
   * a player who drove all the way to a forecourt was still recorded as
   * miles from it -- which is why there was no way to fill up once you got
   * there. The client holds the county, so it can answer this every frame.
   */
  const pumps = county.venues.filter((venue) => venue.kind === 'fuel');

  /** The nearest pump to a point, live. The server still checks the distance. */
  function fuelNear(x: number, y: number): { name: string; node: number; away: number } | null {
    let best: { name: string; node: number; away: number } | null = null;
    for (const pump of pumps) {
      const away = Math.hypot(pump.x - x, pump.y - y);
      if (!best || away < best.away) {
        best = { name: pump.name, node: nearestNode(county, nodeIndex, pump.x, pump.y), away };
      }
    }
    return best;
  }
  /** Whether the car is at the current job's drop, for whoever asks. */
  const atDropNow = (): boolean => {
    const self = net?.selfVehicle;
    if (!self || jobDrop < 0) return false;
    return (
      Math.hypot(
        (county.nodeX[jobDrop] as number) - self.x,
        (county.nodeY[jobDrop] as number) - self.y,
      ) <= ARRIVAL_METERS
    );
  };

  /** The edges last handed to the server, mirrored so the route layer agrees. */
  let avoidedEdges: ReadonlySet<number> = new Set();
  /** Where somebody is navigating to by hand, or -1. Drawn, never driven. */
  let waypoint = -1;

  /**
   * Be driven somewhere.
   *
   * Every path that hands the wheel to the server goes through here -- the map,
   * the job board, the fuel button, a dispatch -- because every one of them
   * ends the hand-navigation it replaces. Being driven somewhere and navigating
   * there yourself are the same journey, and drawing both put two lines on the
   * map in two colors saying the same thing.
   */
  function driveTo(node: number): void {
    waypoint = -1;
    net?.drive(node);
  }

  function toggleAvoid(): void {
    avoiding = !avoiding;
    avoidedEdges = new Set(avoiding ? known.watchedEdges() : []);
    // Off sends an empty set rather than nothing, so the server actually stops
    // avoiding rather than keeping the last list forever.
    net?.avoid(avoiding ? known.watchedEdges() : []);
    hud.say(
      avoiding
        ? `Avoiding ${known.size} cameras. Routes will be longer.`
        : 'Direct routes.',
      6,
    );
  }

  /**
   * File what this plate has driven past.
   *
   * Its own act, which it was not: one button filed your sightings *and*
   * switched the community map on, and said so in a parenthesis — "see all
   * (files your 2)". Those are different things and one of them is publishing.
   * Filing takes what you drove past and puts it on a map other people read,
   * which is a decision, not a side effect of wanting to look at something.
   */
  function fileSightings(): void {
    const all = known.all().map((camera) => camera.id);
    if (all.length === 0) {
      hud.say('Nothing to file yet.', 4);
      return;
    }
    filed = new Set(all);
    net?.report(all);
    paintCameras();
  }

  /** Draw what everybody else has filed, or stop. */
  function toggleCommunity(): void {
    cameraLayer.showCommunity = !cameraLayer.showCommunity;
    if (cameraLayer.showCommunity && community.size === 0) net?.report([]);
    hud.say(
      cameraLayer.showCommunity
        ? community.summary(known.all())
        : 'Showing only what you found.',
      6,
    );
    paintCameras();
  }

  /** Which of this plate's sightings have been published. */
  let filed = new Set<string>();

  function paintCameras(): void {
    cameras?.show({
      found: known.size,
      avoiding,
      unfiled: known.all().filter((camera) => !filed.has(camera.id)).length,
      showingCommunity: cameraLayer.showCommunity,
      reported: community.size,
    });
  }

  function interfere(how: Interference): void {
    interfereWith(how);
  }

  /** Do something to the nearest camera, optionally saying what with. */
  function interfereWith(how: Interference, means?: Obstruction): void {
    const nearest = nearestKnown(known, net);
    if (!nearest) {
      hud.say('Nothing of theirs within reach.', 4);
      return;
    }
    net?.obstruct(nearest.id, how, means);
  }

  /** Open the menu of what can be done to the camera you are standing at. */
  function openCameraMenu(): void {
    const nearest = nearestKnown(known, net);
    if (!nearest) {
      hud.say('Nothing of theirs within reach.', 4);
      return;
    }
    cameraMenu.show(nearest.distance);
  }

  const sim: World | null = solo
    ? createWorld(county, { seed: 20260817, civilianCount: 2200 })
    : null;

  const selectionPan = document.getElementById('selection-pan');

  // Closing the selected-vehicle panel clears the selection. Wired here rather
  // than at the Hud because the selection lives in the input state.
  const input = attachInput(app.canvas, viewport, county, nodeIndex, {
    drive: (node) => driveTo(node),
    stop: () => net?.stop(),
    pick: (px, py, radius) => vehicles.pick(frame, viewport, px, py, radius),
    // Reading a plate off a car is how a query starts. The two halves of the
    // game meeting: you saw it with your eyes, now ask the network about it.
    selected: (id) => {
      // The administrator's inspector rides selection: ask now, and keep
      // asking gently while the panel is open so lights and calls stay live.
      if (adminKey !== undefined) net?.inspect(id);
      const read = net?.reading(id);
      if (read) {
        query?.prefill(read.p);
        alerts?.prefill(read.p);
      }
    },
    pullOver: (id) => net?.pullOver(id),
    toggleAvoid: () => toggleAvoid(),
    steer: (throttle, want) => net?.steer(throttle, want),
    clearWaypoint: () => {
      waypoint = -1;
      hud.say('Waypoint removed.', 3);
    },
    hasWaypoint: () => waypoint >= 0,
    steal: () => net?.steal(),
    // Civilian verb only: an officer on duty commandeering a parked car is a
    // different game, and the server refuses it anyway. Keyed to the live side,
    // not the URL, so switching to authority in-app hides it too.
    canSteal: () => net?.enforcement !== true && (net?.selfVehicle?.speed ?? 1) < 0.5,
    waypoint: (node) => {
      waypoint = node;
      hud.say('Waypoint set. Double tap to be driven there instead.', 5);
    },
    run: () => runAction('run'),
    toggleConsole: () => consolePanel?.toggle(),
    closeConsole: () => {
      if (!consolePanel?.open) return false;
      consolePanel.close();
      return true;
    },
    network: () => runAction('network'),
    share: () => toggleCommunity(),
    interfere: (how) => interfere(how),
    isSolo: solo,
  });

  // Closing the selected-vehicle panel clears the selection. Wired here rather
  // than inside the Hud because the selection lives in the input state, and
  // Escape -- the only other way to clear it -- is no use on a phone.
  /** The last selection the cross-link fired for, so it fires once each. */
  let overlorded = -1;
  /**
   * The administrator's latest look at the selected car: the operator's truth,
   * carried frame to frame so the plate, road and speed stay on screen between
   * the inspector's two-second refreshes rather than flickering back to unread.
   */
  let adminTruth: InspectReplyMessage | null = null;
  hud.whenDeselected(() => {
    input.selectedId = null;
    adminTruth = null;
    hud.inspect(null);
    alerts?.focus(null);
    overlorded = -1;
    if (selectionPan) selectionPan.hidden = true;
  });

  // Pan the map to the selected car, wherever it is in the county.
  selectionPan?.addEventListener('click', () => {
    if (adminTruth) viewport.centerOnWorld(adminTruth.x, adminTruth.y);
  });

  // Keep the inspector current while a car stays selected. Two seconds is
  // enough for lights and calls to read live without chatter.
  if (adminKey !== undefined) {
    let lastInspected = -1;
    window.setInterval(() => {
      if (input.selectedId === null) {
        if (lastInspected !== -1) {
          lastInspected = -1;
          hud.inspect(null);
        }
        return;
      }
      lastInspected = input.selectedId;
      net?.inspect(input.selectedId);
    }, 2000);
  }

  // Keep the oversight dashboard's aggregates current while it is open. The live
  // feed streams on its own; the cards -- reads, stops, agency ledgers -- are a
  // snapshot, so they are re-asked for on a slow timer rather than left to age.
  if (adminKey !== undefined) {
    window.setInterval(() => {
      if (consolePanel?.open) net?.admin();
    }, 10000);
    // While riding along, keep the player's state fresh -- money, job, speed,
    // whether they are being chased -- so the readout tracks what they do.
    window.setInterval(() => {
      if (input.rideAlongId !== null) net?.rideAlong(input.rideAlongId);
    }, 900);
  }

  // "Join car": the operator drops into a player's seat -- the map locks to
  // their car, the dashboard steps aside, and a slim HUD shows their state,
  // their mission and their moves as they make them. It is not their literal
  // screen (that is never sent) but it is their ride.
  let joinedId: number | null = null;
  let lastJoin: RideAlongStateMessage | null = null;
  let priorScale = viewport.scale;
  const joinHud = document.getElementById('join-hud');
  const joinRows = document.getElementById('join-rows');
  const joinPlate = document.getElementById('join-plate');
  const joinStatus = document.getElementById('join-status');
  const joinMission = document.getElementById('join-mission');
  const joinFuelVal = document.getElementById('join-fuel-val');
  const joinFuelFill = document.getElementById('join-fuel-fill');
  const joinTicker = document.getElementById('join-ticker');
  document.getElementById('join-exit')?.addEventListener('click', () => exitJoin());

  function startJoin(id: number): void {
    joinedId = id;
    lastJoin = null;
    input.selectedId = id;
    input.rideAlongId = id;
    input.following = true;
    priorScale = viewport.scale;
    viewport.scale = Math.max(viewport.scale, 1.5);
    consolePanel?.close();
    if (joinTicker) joinTicker.replaceChildren();
    document.body.classList.add('joined');
    if (joinHud) joinHud.hidden = false;
    net?.rideAlong(id);
  }
  function exitJoin(): void {
    joinedId = null;
    input.rideAlongId = null;
    input.following = false;
    viewport.scale = priorScale;
    document.body.classList.remove('joined');
    if (joinHud) joinHud.hidden = true;
    const self = net?.selfVehicle;
    if (self) viewport.centerOnWorld(self.x, self.y);
    if (!consolePanel?.open) consolePanel?.toggle();
  }
  function joinTick(message: string): void {
    if (!joinTicker) return;
    const row = document.createElement('div');
    row.className = 'join-tick';
    row.textContent = message;
    joinTicker.prepend(row);
    while (joinTicker.childElementCount > 12) joinTicker.lastElementChild?.remove();
  }
  function updateJoin(s: RideAlongStateMessage): void {
    if (joinedId === null || s.id !== joinedId) return;
    if (joinPlate) joinPlate.textContent = s.plate;
    if (joinStatus) {
      joinStatus.textContent = s.pursued ? 'BEING CHASED' : s.wanted ? 'WANTED' : 'clear';
      joinStatus.className = 'join-status' + (s.pursued ? ' chased' : s.wanted ? ' wanted' : '');
    }
    if (joinRows) {
      joinRows.replaceChildren();
      const earned = s.earnedLegal + s.earnedIllegal;
      const pairs: [string, string][] = [
        ['on', `${s.road} - ${s.mph} mph`],
        ['money', `$${s.money.toLocaleString()}`],
        ['earned', `$${earned.toLocaleString()}`],
      ];
      for (const [k, v] of pairs) {
        const r = document.createElement('div');
        r.className = 'join-row';
        const kk = document.createElement('span');
        kk.textContent = k;
        const vv = document.createElement('span');
        vv.className = 'join-v';
        vv.textContent = v;
        r.append(kk, vv);
        joinRows.append(r);
      }
    }
    // Their tank, not the operator's -- the ride-along mirrors their dash.
    if (joinFuelFill && joinFuelVal) {
      const share = s.fuelCap > 0 ? Math.max(0, Math.min(1, s.fuel / s.fuelCap)) : 0;
      joinFuelFill.style.width = `${(share * 100).toFixed(0)}%`;
      joinFuelFill.className =
        'join-fuel-fill' + (share <= 0.15 ? ' empty' : share <= 0.33 ? ' low' : '');
      joinFuelVal.textContent = `${s.fuel.toFixed(1)} / ${s.fuelCap} gal`;
    }
    if (joinMission) {
      joinMission.replaceChildren();
      if (s.job) {
        const title = document.createElement('div');
        title.className = 'join-mission-title' + (s.job.illegal ? ' illegal' : '');
        title.textContent = (s.job.illegal ? 'ILLEGAL JOB' : 'legal job') + ` - $${s.job.pays}`;
        const steps = document.createElement('div');
        steps.className = 'join-steps';
        const collect = document.createElement('div');
        collect.className = 'join-step ' + (s.job.stage === 'collect' ? 'active' : 'done');
        collect.textContent = '1 · collect · ' + s.job.from;
        const deliver = document.createElement('div');
        deliver.className = 'join-step ' + (s.job.stage === 'deliver' ? 'active' : '');
        deliver.textContent = '2 · deliver · ' + s.job.to;
        steps.append(collect, deliver);
        joinMission.append(title, steps);
      } else {
        const idle = document.createElement('div');
        idle.className = 'join-idle';
        idle.textContent = 'No job right now.';
        joinMission.append(idle);
      }
    }
    if (lastJoin) {
      const a = lastJoin;
      if (!a.job && s.job) joinTick(`took a ${s.job.illegal ? 'illegal' : 'legal'} job -> ${s.job.to}`);
      if (a.job && s.job && a.job.stage === 'collect' && s.job.stage === 'deliver')
        joinTick(`collected at ${s.job.from} -- heading to ${s.job.to}`);
      if (a.job && !s.job) joinTick('delivered -- job done');
      if (!a.wanted && s.wanted) joinTick('went on the hotlist');
      if (!a.pursued && s.pursued) joinTick('a unit lit them up');
      if (a.mph < 1 && s.mph >= 1) joinTick('started driving');
      if (a.mph >= 1 && s.mph < 1) joinTick('stopped');
    }
    lastJoin = s;
  }

  /**
   * One place every action goes through.
   *
   * The buttons and the keys call the same function, so a phone and a keyboard
   * cannot drift apart -- which they would the moment somebody added an action
   * to one and forgot the other, and that is exactly how the phone build ended
   * up with no way to do anything.
   */
  function runAction(name: ActionName): void {
    switch (name) {
      case 'handin':
        net?.job('arrive');
        return;
      case 'jobdrive':
        if (jobDrop >= 0) driveTo(jobDrop);
        return;
      case 'fuel':
        // At the pump this fills the tank; anywhere else it drives you there.
        if (nearestFuel && nearestFuel.away <= REFUEL_METERS) net?.buy('fuel');
        else if (nearestFuel) driveTo(nearestFuel.node);
        return;
      case 'camera':
        openCameraMenu();
        return;
      case 'stop':
        net?.stop();
        return;
      case 'follow':
        input.following = !input.following;
        return;
      case 'pullover':
        if (input.selectedId !== null) net?.pullOver(input.selectedId);
        return;
      case 'pursue':
        if (input.selectedId !== null) net?.pursue(input.selectedId);
        return;
      case 'breakoff':
        net?.breakoff();
        return;
      case 'sendunit': {
        const read = input.selectedId !== null ? net?.reading(input.selectedId) : null;
        if (read) net?.dispatchUnit(read.p);
        return;
      }
      case 'avoid':
        toggleAvoid();
        return;
      case 'obstruct':
      case 'damage':
      case 'take':
        interfere(name);
        return;
      case 'share':
        toggleCommunity();
        return;
      case 'network':
        net?.share(!networked);
        return;
      case 'run':
        fast = !fast;
        net?.run(fast);
        return;
    }
  }

  // -- the pursuit HUD -------------------------------------------------------
  const pursuitEl = document.getElementById('pursuit');
  const pursuitWordEl = document.getElementById('pursuit-word');
  const pursuitPlateEl = document.getElementById('pursuit-plate');
  const pursuitDistEl = document.getElementById('pursuit-dist');
  const pursuitTrendEl = document.getElementById('pursuit-trend');
  /** The gap the trend last flipped at, so closing/opening does not flicker. */
  let pursuitAnchor = -1;
  let pursuitTrend = '';

  // The quarry's clocked speed, when a radar gun is fitted: derived from how far
  // it moved across the frame, which is exactly what a radar reads -- a speed off
  // a moving object in front of you.
  let quarryPrevX = 0;
  let quarryPrevY = 0;
  let quarryPrevT = 0;
  let quarryMph = 0;
  let lastQuarryId = -1;

  /**
   * Paint the chase. Eyes on: who, how far, which way the gap is moving, and --
   * with a radar gun -- how fast they are going. Lost: the last heading and
   * place to search off, because there is no live car to measure.
   */
  function updatePursuitHud(drawn: Renderable[]): void {
    if (!pursuitEl) return;
    const chase = net?.pursuit ?? null;
    if (!chase) {
      if (!pursuitEl.hidden) pursuitEl.hidden = true;
      pursuitEl.classList.remove('lost');
      pursuitAnchor = -1;
      pursuitTrend = '';
      return;
    }
    pursuitEl.hidden = false;
    if (pursuitPlateEl) pursuitPlateEl.textContent = chase.plate;

    // Lost: the officer is searching. No live distance to a car nobody can see --
    // show where it was last, and lean on the camera pins.
    if (chase.sight === 'lost') {
      pursuitEl.classList.add('lost');
      if (pursuitWordEl) pursuitWordEl.textContent = 'SEARCHING';
      if (pursuitDistEl) pursuitDistEl.textContent = `last seen ${headingWord(chase.lastHeading)}`;
      if (pursuitTrendEl) {
        pursuitTrendEl.textContent = chase.lastPlace ? `near ${chase.lastPlace}` : 'watch the cameras';
        pursuitTrendEl.className = 'pursuit-trend';
      }
      pursuitAnchor = -1;
      return;
    }
    pursuitEl.classList.remove('lost');
    if (pursuitWordEl) pursuitWordEl.textContent = 'PURSUING';

    const me = drawn.find((r) => r.self);
    const target = drawn.find((r) => r.id === chase.target);
    if (!me || !target) {
      if (pursuitDistEl) pursuitDistEl.textContent = 'out of sight';
      if (pursuitTrendEl) {
        pursuitTrendEl.textContent = '';
        pursuitTrendEl.className = 'pursuit-trend';
      }
      return;
    }

    // Radar clock: the quarry's speed off its frame-to-frame movement.
    const nowMs = performance.now();
    if (chase.target === lastQuarryId && quarryPrevT > 0) {
      const dt = (nowMs - quarryPrevT) / 1000;
      if (dt > 0.05) {
        const inst =
          (Math.hypot(target.x - quarryPrevX, target.y - quarryPrevY) / dt) * 2.23694;
        quarryMph += (inst - quarryMph) * 0.25;
      }
    } else {
      quarryMph = 0;
    }
    lastQuarryId = chase.target;
    quarryPrevX = target.x;
    quarryPrevY = target.y;
    quarryPrevT = nowMs;

    const meters = Math.hypot(me.x - target.x, me.y - target.y);
    if (pursuitDistEl) {
      const gap =
        meters >= 305
          ? `${(meters / 1609.34).toFixed(1)} mi`
          : `${Math.round((meters * 3.28084) / 10) * 10} ft`;
      pursuitDistEl.textContent = net?.radar ? `${gap} · ${Math.round(quarryMph)} mph` : gap;
    }
    if (pursuitAnchor < 0) pursuitAnchor = meters;
    else if (meters <= pursuitAnchor - 4) {
      pursuitTrend = 'closing';
      pursuitAnchor = meters;
    } else if (meters >= pursuitAnchor + 4) {
      pursuitTrend = 'opening';
      pursuitAnchor = meters;
    }
    if (pursuitTrendEl) {
      pursuitTrendEl.textContent =
        pursuitTrend === 'closing' ? '▼ closing' : pursuitTrend === 'opening' ? '▲ opening' : '';
      pursuitTrendEl.className = `pursuit-trend${pursuitTrend ? ` ${pursuitTrend}` : ''}`;
    }
  }


  const net: NetClient | null = solo
    ? null
    : new NetClient({
        county,
        url: serverUrl(),
        // Held while the front door is open: no connection, no car, no join
        // until a door is chosen.
        holdConnection: atTheDoor,
        enforcement: wantsNetwork,
        ...(adminKey !== undefined ? { admin: adminKey } : {}),
        onResult: (result) =>
          query?.show(result.by, result.term, result.rows, result.truncated, result.millis),
        onAlert: (alert) => {
          alerts?.receive(alert);
          consolePanel?.notify('alerts');
        },
        onHotlist: (entries) => alerts?.showHotlist(entries),
        onAudit: (entries, total, unexplained) => audit?.show(entries, total, unexplained),
        onNotice: (message) => hud.say(message, 7),
        // Server-driven, so the scope line cannot disagree with the scope.
        onSources: (sources) => citations.show(sources),
        // The ring changed -- a car registered, or a keyring arrived from
        // another device -- so whatever is showing it has to be redrawn.
        onKeyring: () => garage?.show(net?.cars ?? [], net?.plate?.text ?? ''),
        /**
         * The administrator's inspector answers land here -- and a listed car
         * cross-links the whole record. Selecting it opens the terminal on
         * Alerts with this plate's rows pulsing and the search console
         * pre-filled, so "what is going on with this car" is one click deep
         * instead of a plate memorized and retyped across three panels.
         *
         * Once per selection, because the inspector refreshes every two
         * seconds and a console that re-yanks itself while the operator is
         * reading is a console that gets closed.
         */
        onInspect: (told) => {
          adminTruth = told;
          hud.inspect(told);
          // The selected car can sit outside the operator's view; the button
          // pans straight to the position the inspector just reported.
          if (selectionPan) selectionPan.hidden = told.id !== input.selectedId;
          if (told.id !== input.selectedId || told.id === overlorded) return;
          overlorded = told.id;
          query?.prefill(told.plate);
          if (told.wanted && alerts?.focus(told.plate)) {
            if (!consolePanel?.open) consolePanel?.toggle();
            consolePanel?.select('alerts');
          }
        },
        /**
         * A remembered key just got refused and dropped, which makes this
         * browser new again in every way that matters. The landing card comes
         * back so the fresh car about to arrive gets the same front door --
         * and the same save-on-Drive -- as a first visit. Without this, the
         * player who hit a wiped server was just silently in some car with no
         * card and no way to understand why.
         */
        onKeyDropped: () => showLanding?.(),
        onPlatesForSale: () => consolePanel?.revealStore(),
        onPlateOffer: (plate, ok, reason, priceCents) => {
          // The first offer of any kind means the server sells plates.
          consolePanel?.revealStore();
          garage?.plateVerdict(plate, ok, reason, priceCents);
        },
        onPlateCheckout: (url) => {
          // The checkout URL comes back from the server, so opening it happens a
          // round-trip AFTER the tap -- outside the user gesture. On a phone that
          // is a blocked popup: some browsers return null (the redirect fallback
          // saves it), but others hand back a dead blank window that never
          // navigates, so the fallback never fires and nothing happens. That is
          // the whole "store does not work on mobile" bug. On a touch device,
          // skip the popup and just redirect -- Stripe returns through its own
          // success URL and the webhook registers the plate either way. On a
          // desktop the sized popup still works and keeps the game socket alive.
          const onPhone =
            window.matchMedia?.('(pointer: coarse)').matches || 'ontouchstart' in window;
          if (onPhone) {
            location.href = url;
            return;
          }
          const popup = window.open(url, 'hotlist-plate', 'width=480,height=760');
          if (!popup) location.href = url;
        },
        /**
         * A saved passkey is the ticket through the gate. When the save that
         * the door asked for lands, this is where it lands, so this is where
         * the overlay lifts. Outside the door ceremony it does nothing.
         */
        onPasskeys: (saved) => {
          if ((door === 'enroll' || door === 'signin') && saved > 0) enterGame();
        },
        onPlate: (plate) => hud.say(`${plate.text} is yours.`, 5),
        onWeek: (week) => yourWeek.show(week, net?.time ?? 0),
        onStanding: (report) => standingPanel?.show(report),
        onScanner: (call) =>
          scanner?.hear(call, formatClock(call.at, worldDaySeconds)),
        onAdminState: (state) => admin?.show(state),
        onOversight: (plate, incidents, journey) =>
          admin?.showOversight(plate, incidents, journey),
        onHeatmap: (cells, hours) => {
          heatmapLayer.setCells(cells);
          showHeatHours(hours);
        },
        onAdminEvent: (event) => admin?.event(event),
        onRideAlong: (rideState) => {
          admin?.showRideAlong(rideState);
          updateJoin(rideState);
          // Interest management only sends the admin the vehicles near its own
          // viewport, so a player anywhere else is never in the frame and the
          // per-frame follow has nothing to lock onto -- the map just sat on the
          // admin's own car. The reply carries the player's position, so center
          // on it directly: that both moves the map onto them and pulls their
          // car into the interest set, after which the follow tracks it live.
          if (input.rideAlongId === rideState.id) {
            viewport.centerOnWorld(rideState.x, rideState.y);
          }
        },
        onCameraDetail: (cam) => {
          // Visiting a camera stops any follow and drops the operator onto the
          // pole itself, then shows what it is watching.
          input.rideAlongId = null;
          input.following = false;
          viewport.centerOnWorld(cam.x, cam.y);
          admin?.showCameraDetail(cam);
        },
        onResetDone: (what, said) => {
          admin?.said(said);
          hud.say(said, 12);
          // The county underneath just changed, so anything the client had
          // worked out about it is now about a world that no longer exists.
          if (what !== 'players') {
            known.forget();
            community.set([], 0);
            cameraLayer.showCommunity = false;
          }
          net?.admin();
        },
        onSideChanged: (side) => {
          consolePanel?.setRole(side.enforcement);
          // The vehicle panel shows a different set of rows on each side.
          hud.setRole(side.enforcement);
          cameraLayer.setEnforcement(side.enforcement);
          consolePanel?.setShift(
            side.shiftEnds ?? 0,
            side.restEnds ?? 0,
            net?.time ?? 0,
            worldDaySeconds,
          );
          if (side.agency !== undefined) {
            query?.setPosting({
              agency: side.agency,
              agencyCameras: side.agencyCameras ?? 0,
              countyCameras: side.countyCameras ?? 0,
            });
          }
          hud.say(
            side.enforcement
              ? `${side.agency ?? 'The console'}. ${side.agencyCameras ?? 0} cameras of its own, ` +
                `${side.countyCameras ?? 0} in the county.`
              : 'Back to civilian.',
            8,
          );
        },
        onJobBoard: (board) => {
          money = board.money;
          hasJob = board.current !== null;
          // Where they are headed *now*, which is the pickup on the first leg.
          jobDrop = board.target?.node ?? -1;
          jobPays = board.current?.pays ?? 0;
          jobDropName = board.target?.name ?? '';
          jobCollecting = board.stage !== 'deliver' && board.current !== null;
          tank = board.tank;
          nearestFuel = board.nearestFuel
            ? {
                name: board.nearestFuel.brand || board.nearestFuel.name,
                node: board.nearestFuel.node,
                away: board.nearestFuel.away,
              }
            : null;
          markers.setJob(board.current, board.stage ?? 'collect');
          hud.setWanted(board.wanted);
          // The patrol kit line: whether the radar is fitted and what the
          // department can spend on one.
          dispatch?.setKit(board.radar === true, RADAR_PRICE, board.agencyRevenue ?? 0);
          // Painted last, and only once the state above is current: it used to
          // be painted first, so the panel answered "are we there yet" about
          // the *previous* job's destination.
          work?.show(board, atDropNow());
          // The garage sells the tag and the car, so it needs the balance.
          garage?.setPurse(
            board.money,
            board.wanted === true,
            board.garageCapacity ?? 1,
            board.carsOwned ?? 1,
          );
          scanner?.setOwned(board.scanner === true);
        },
        onDispatchBoard: (board) => {
          pendingDispatch.clear();
          for (const open of board.open) pendingDispatch.set(open.camera, { x: open.x, y: open.y });
          dispatch?.show(board);
          if (board.open.length > 0) consolePanel?.notify('dispatch');
        },
        onSharing: (on) => {
          networked = on;
          query?.setSharing(on);
        },
        onCommunityMap: (markers, yours) => {
          community.set(markers, yours);
          paintCameras();
          cameraLayer.showCommunity = true;
          hud.say(community.summary(known.all()), 10);
        },
        onCameras: (seen, scope) => {
          if (scope === 'coverage') {
            // An operator's own deployment. Never merged into the found map:
            // that would hand this player a free map the moment they signed off.
            coverage.set(seen);
            if (seen.length > 0) {
              hud.say(`${seen.length} cameras on your department's map.`, 6);
            }
            return;
          }
          const fresh = known.add(seen);
          paintCameras();
        if (fresh === 0) return;
          hud.say(
            fresh === 1
              ? `Camera found. ${known.size} known.`
              : `${fresh} cameras found. ${known.size} known.`,
            5,
          );
          // The server only routes around what this client tells it to, and
          // this client only knows what it drove past.
          if (avoiding) {
            const edges = known.watchedEdges();
            avoidedEdges = new Set(edges);
            net?.avoid(edges);
          }
        },
        onStopResult: (result) => {
          // The moment it stops being records and becomes a person. What
          // decides it is the plate the car actually carries, not the one the
          // camera reported and not the one you believed.
          const tally =
            `That is ${result.wrongfulCount} wrongful ` +
            `${result.wrongfulCount === 1 ? 'stop' : 'stops'} of ${result.stopCount}.`;

          // Lawful, whatever the alert said. The tag is its own reason and it
          // is the commonest one there is.
          if (result.expired && !result.onHotlist) {
            hud.say(
              `${result.plate} was not on the hotlist. Registration expired. ${tally}`,
              10,
            );
            return;
          }

          if (result.mismatched) {
            // The Aurora case. Both fields were on the alert.
            hud.say(
              `Plate matched, vehicle did not. Record wanted a ${result.wanted}; ` +
                `you stopped a ${result.found}. ${tally}`,
              12,
            );
          } else if (result.stale) {
            hud.say(
              `${result.plate} was on the list for something already resolved. ${tally}`,
              12,
            );
          } else if (result.wrongful) {
            hud.say(`${result.plate} was not on the hotlist. ${tally}`, 10);
          } else {
            hud.say(`${result.plate}: ${result.reason}. Vehicle matches.`, 6);
          }
        },
        onPursuit: (p) => {
          // The start carries the speed allowance; a mid-chase update carries a
          // sight change; the end carries a reason. The live HUD does distance
          // and direction every frame -- these are just the moments worth a word.
          if (p.active && p.speedMultiplier !== undefined) {
            hud.say(`Pursuing ${p.plate}. Lights on — closing in.`, 5);
          } else if (p.active && p.sight === 'lost') {
            hud.say(
              `Lost sight of ${p.plate}. Last seen ${headingWord(p.lastHeading)}` +
                `${p.lastPlace ? ` near ${p.lastPlace}` : ''}. Watch the cameras.`,
              8,
            );
          } else if (p.active && p.sight === 'eyes') {
            hud.say(`Back on ${p.plate}.`, 4);
          } else if (p.ended === 'eluded') {
            hud.say(`${p.plate} got away.`, 6);
          } else if (p.ended === 'off') {
            hud.say('Pursuit broken off.', 4);
          }
        },
        onWelcome: (plate, pin, note, daySeconds, posting) => {
          /**
           * The door's two follow-throughs.
           *
           * Drive asked for the save; the first welcome is the first moment a
           * car exists to save, so the device unlock is asked for here, still
           * inside the click's user-activation window on any sane connection.
           * A sign-in in progress instead sends the ceremony request -- and if
           * this welcome carries a pin during one, it IS the sign-in landing
           * (the server seats you and cuts a fresh resume key), so the door
           * closes on your own car.
           */
          /**
           * At the gate, a welcome carrying a pin is a sign-in that landed --
           * the server seated you in your own car and cut a fresh resume key,
           * so the gate lifts. A welcome without a pin is the ephemeral guest
           * seat that every connection gets; it is ignored, because a guest
           * seat is not a saved game and the gate does not open for it. The
           * save path lands in onPasskeys, not here.
           */
          if (door === 'signin' && pin) {
            // A sign-in that seated your own car. The gate lifts.
            enterGame();
          } else if (door === 'signin') {
            // The guest welcome that every connection gets, arriving just after
            // a door click. There is no car to lift the gate for yet, so this
            // is where the sign-in ceremony actually begins -- on the socket,
            // once, kicked by the first welcome rather than the click, because
            // the click fired before the connection existed.
            net?.passkey('open');
          } else if (door === 'closed' && net?.justResumed) {
            // A returning browser's key was honored while the door stood open:
            // this is their own car arriving, so the door lifts with no
            // ceremony. Keyless and dead-key browsers never reach here.
            enterGame();
          }
          if (daySeconds) worldDaySeconds = daySeconds;
          if (daySeconds) query?.setDaySeconds(daySeconds);
          if (daySeconds) audit?.setDaySeconds(daySeconds);
          if (daySeconds) dispatch?.setDaySeconds(daySeconds);
          if (daySeconds) yourWeek.setDaySeconds(daySeconds);
          if (daySeconds) hud?.setDaySeconds(daySeconds);
          if (daySeconds) admin?.setDaySeconds(daySeconds);
          if (daySeconds) journeyLayer.setDaySeconds(daySeconds);
          // The found map belongs to the plate. The server sends back what this
          // one has driven past; this points the local cache at the same car.
          known.setPlate(plate.text);
          cameraLayer.setEnforcement(net?.enforcement === true);
          // A reconnect (a mid-session server restart re-opens the socket with
          // no page reload) must re-sync WHICH SIDE the interface is on, not
          // just whether the console is enabled. Without these two, a client
          // that dropped on one side and came back on the other kept the old
          // side's console tabs, vehicle-panel rows and marked-unit sprite --
          // the half-authority state where civilian jobs run under a cop car.
          consolePanel?.setRole(net?.enforcement === true);
          hud.setRole(net?.enforcement === true);
          if (adminKey !== undefined) {
            consolePanel?.setAdministrator(true);
            net?.admin();
          }
          // A note already names the plate, so repeating it reads like a stutter.
          //
          // The resume key is deliberately NOT shown. It is an internal token:
          // the server cuts one on sign-in and the client stores it silently
          // (net.ts) so the same browser can auto-resume without a passkey
          // ceremony next time. It changes every sign-in and the browser keeps
          // it, so flashing it at the player only invited them to write down a
          // number that is already saved and about to be replaced -- and this
          // game's recovery is the passkey, with a pairing code across devices,
          // never a hand-copied key (that spare-key idea was rejected on
          // purpose). So arrival just names the car.
          // The ephemeral guest seat every connection is handed (no pin, no
          // resume) is not a car you are "back" in -- it is the placeholder
          // that arrives a beat before your own car does, and naming it put a
          // random plate in the log on every single login. Only a real seating
          // gets named: a sign-in that landed (pin) or an auto-resume.
          if (note) hud.say(note, 9);
          else if (pin || net?.justResumed) hud.say(`Back in ${plate.text}.`, 4);

          // The console is everybody's. Which tabs it holds is not.
          consolePanel?.enable(net?.enforcement === true);
          garage?.show(net?.cars ?? [], plate.text);

          // Work is everybody's, so this is asked for outside the enforcement
          // branch it was sitting in -- where no civilian ever reached it, and
          // a civilian is exactly who the work board is for.
          net?.job();

          viewport.scale = Math.max(viewport.scale, 0.22);
          input.following = true;
          if (net?.enforcement) {
            query?.enable();
            alerts?.enable();
            audit?.enable();
            dispatch?.enable();
            net?.dispatch();
            net?.standing();
            if (posting) {
              query?.setPosting(posting);
              hud.say(
                `${posting.agency}. ${posting.agencyCameras} cameras of its own, ` +
                  `${posting.countyCameras} in the county.`,
                10,
              );
            } else {
              hud.say(
                'You have the network.',
                6,
              );
            }
          }
        },
        onError: (message, about) => {
          /**
           * The gate's ceremony, staged.
           *
           * Sign-in failed or was cancelled: this device has no game to sign
           * into, so the only way through the gate is to make one. The create
           * dialog is asked for on the same activation, and the overlay does
           * not move. Create failed or was cancelled: the gate stays shut and
           * says so, with the buttons live to try again. There is no path here
           * that reaches the game, which is the whole point.
           */
          if (about === 'passkey' && door === 'signin') {
            door = 'enroll';
            doorSaid('Save your game to start playing...');
            net?.passkey('save');
            return;
          }
          if (about === 'passkey' && door === 'enroll') {
            door = 'blocked';
            setDoorBusy(false);
            doorSaid('You have to save your game to play. Pick a side to try again.');
            return;
          }
          // Routed by subject. Anything without one belongs to the world, and
          // the world's messages go where the player is looking, which is the
          // road rather than a panel.
          if (about === 'query' && query?.enabled) query.fail(message);
          else if (about === 'audit' && audit) audit.fail(message);
          else hud.say(message, 5);
        },
      });

  let accumulator = 0;
  let last = performance.now();
  let fps = 60;

  app.ticker.add(() => {
    const now = performance.now();
    const elapsed = Math.min(0.25, (now - last) / 1000);
    last = now;
    fps += (1 / Math.max(elapsed, 1e-4) - fps) * 0.06;

    if (app.screen.width !== viewport.width || app.screen.height !== viewport.height) {
      viewport.resize(app.screen.width, app.screen.height);
    }

    let model: HudModel;

    if (sim) {
      if (!input.paused) {
        accumulator += elapsed * (SIM_SPEEDS[input.speedIndex] as number);
        let steps = 0;
        // Cap the catch-up so a background tab does not come back and run
        // several minutes of simulation in one frame.
        while (accumulator >= TICK_SECONDS && steps < 240) {
          tickWorld(sim);
          accumulator -= TICK_SECONDS;
          steps++;
        }
        if (steps >= 240) accumulator = 0;
      }
      const alpha = input.paused ? 1 : Math.min(1, accumulator / TICK_SECONDS);
      frame = soloRenderables(sim, alpha);
      model = soloModel(sim, county, frame, input, fps);
    } else if (net) {
      net.update(elapsed);
      frame = net.renderables();
      model = liveModel(net, county, frame, input, fps, adminTruth, {
        jobDrop,
        jobDropName,
        fuel: nearestFuel,
      });
    } else {
      return;
    }

    if (input.following) {
      const target =
        input.rideAlongId !== null
          ? frame.find((r) => r.id === input.rideAlongId)
          : net
            ? frame.find((r) => r.self)
            : input.selectedId !== null
              ? frame.find((r) => r.id === input.selectedId)
              : undefined;
      if (target) viewport.centerOnWorld(target.x, target.y);
    }
    viewport.clampTo(county);

    // Close enough to hand the job in, which both the action bar and the work
    // panel need to agree about.
    const atDrop =
      jobDrop >= 0 &&
      (net?.selfVehicle ?? null) !== null &&
      Math.hypot(
        (county.nodeX[jobDrop] as number) - (net?.selfVehicle?.x ?? 0),
        (county.nodeY[jobDrop] as number) - (net?.selfVehicle?.y ?? 0),
      ) <= ARRIVAL_METERS;
    work?.setAtDrop(atDrop);

    // What is possible right now, which is the whole interface on a phone.
    const nearest = nearestKnown(known, net);
    const self = net?.selfVehicle ?? sim?.vehicles[0] ?? null;
    // Live, from where the car is now, rather than from whenever a job board
    // last went out.
    if (self) nearestFuel = fuelNear(self.x, self.y);
    const selected =
      input.selectedId !== null ? (net?.reading(input.selectedId) ?? null) : null;
    actions.update({
      driving: (self?.speed ?? 0) > 0.2 || (self?.destination ?? -1) >= 0,
      following: input.following,
      // Close enough to read a plate is close enough to stop the car, which is
      // the server's rule as well; it decides, this only offers.
      canPullOver: selected !== null,
      selectedPlate: selected?.p ?? '',
      // A car is selected at all -- enough to give chase. You can light up a car
      // whose plate you cannot read yet; that is the whole point of a pursuit.
      hasSelection: input.selectedId !== null && input.selectedId !== (self?.id ?? -1),
      pursuing: net?.pursuit !== null && net?.pursuit !== undefined,
      pursuePlate: selected?.p ?? '',
      nearCamera: nearest !== null,
      cameraDistance: nearest?.distance ?? Infinity,
      hasMap: known.size > 0,
      mapSize: known.size,
      showingCommunity: cameraLayer.showCommunity,
      avoiding,
      enforcement: net?.enforcement === true,
      networked,
      isAdmin: adminKey !== undefined,
      running: fast,
      hasJob,
      atDrop,
      jobPays,
      jobTo: jobDropName,
      collecting: jobCollecting,
      // Already headed there, so there is nothing to offer.
      onJobRoute: jobDrop >= 0 && (self?.destination ?? -1) === jobDrop,
      fuelShare: tank > 0 ? (net?.selfVehicle?.fuel ?? 0) / tank : 1,
      fuelStop: nearestFuel?.name ?? '',
      atPump: (nearestFuel?.away ?? Infinity) <= REFUEL_METERS,
    });

    // The chase, if there is one: who and how far, read off the same frame the
    // map draws so the gap is exactly what is on screen. The cruiser itself is
    // steered by the server (tickPursuits), which follows the quarry live.
    updatePursuitHud(frame);

    // The gauge runs off the snapshot, so it moves as the car does rather than
    // when somebody happens to open a panel.
    hud.setFuel(net?.selfVehicle?.fuel ?? 0, tank, nearestFuel ?? undefined);

    // Anything with its lights on, which the layer already knows about because
    // it has to draw them. Distance does the rest.
    sound.update(
      net?.selfVehicle ?? null,
      frame.filter((item) => item.lights === true),
    );
    // What is on screen, so the server sends the traffic that is in it rather
    // than a disc of it around the player.
    if (net) {
      const seen = viewport.visibleBounds(0);
      const a = unproject(projection, seen.minX, seen.minY);
      const b = unproject(projection, seen.maxX, seen.maxY);
      const c = unproject(projection, seen.minX, seen.maxY);
      const d = unproject(projection, seen.maxX, seen.minY);
      net.view(
        Math.min(a.x, b.x, c.x, d.x),
        Math.min(a.y, b.y, c.y, d.y),
        Math.max(a.x, b.x, c.x, d.x),
        Math.max(a.y, b.y, c.y, d.y),
      );
    }
    satellite.update(viewport);
    roads.update(viewport);
    if (net) {
      routeLayer.update(
        viewport,
        net.selfVehicle,
        net.router,
        avoiding,
        // Exactly the set this client asked the server to route around, which
        // is exactly what it found by driving. No coverage can leak through a
        // line drawn from a set the client already had.
        avoidedEdges,
        waypoint,
      );
    }
    cameraLayer.update(viewport);
    markers.update(viewport);
    vehicles.update(frame, viewport, input.selectedId);
    // The county clock, either the local sim's or the last snapshot's, drives
    // the light over the whole map.
    dayNight.update(
      sim ? sim.time : (net?.time ?? 0),
      sim ? sim.daySeconds : worldDaySeconds,
      app.screen.width,
      app.screen.height,
    );
    heatmapLayer.update(viewport);
    journeyLayer.update(viewport);
    alerts?.update(now);
    hud.update(model, now);
  });

  boot.classList.add('gone');
  window.setTimeout(() => {
    boot.hidden = true;
  }, 260);

  Object.assign(window as unknown as Record<string, unknown>, {
    hotlist: { app, sim, net, viewport, county, roads, vehicles, input, projection, hud, nodeIndex, actions, known },
  });
}

// -- turning state into what the HUD reads -----------------------------------

function soloRenderables(world: World, alpha: number): Renderable[] {
  const items: Renderable[] = [];
  for (const vehicle of world.vehicles) {
    items.push({
      id: vehicle.id,
      x: vehicle.prevX + (vehicle.x - vehicle.prevX) * alpha,
      y: vehicle.prevY + (vehicle.y - vehicle.prevY) * alpha,
      heading: vehicle.heading,
      kind: vehicle.kind,
    });
  }
  return items;
}

function describe(
  county: County,
  vehicle: Vehicle,
  plate: string,
  jurisdiction: string,
): VehicleInfo {
  const onRoad = vehicle.edge >= 0;
  const left = routeRemaining(county, vehicle.route, vehicle.routeIndex, vehicle.offset);
  return {
    how: 'in-sight',
    plate,
    jurisdiction,
    /**
     * The road you are on, standing still or not.
     *
     * There is nowhere in this game that is not a road -- cars live on the
     * graph as (edge, offset) and never in free space -- so "parked" and "off
     * the road" were both answering a question nobody asked. Stopping the car
     * erased where you were, which is the moment you are most likely to be
     * looking at the panel. Being stationary is what the speed row is for.
     *
     * A stopped car reports no edge in the snapshot, so the road is resolved
     * from where it is standing.
     */
    road: onRoad
      ? signedRoad(county, vehicle.edge, vehicle.heading)
      : roadNear(county, vehicle.x, vehicle.y),
    // And which town, which nothing said at all. The county is thirty
    // miles across and a road name on its own does not place you.
    town: placeAt(county, vehicle.x, vehicle.y),
    roadClass: onRoad ? className(county.edgeClass[vehicle.edge] as number) : '',
    mph: Math.round(vehicle.speed * 2.23694),
    // The posted limit of this edge, resolved the way the server does: the
    // per-edge value when it has one, a sane default when the class carries it.
    postedMph: onRoad
      ? Math.round(
          ((county.edgeSpeed[vehicle.edge] as number) > 0
            ? (county.edgeSpeed[vehicle.edge] as number)
            : 13.4) * 2.23694,
        )
      : 0,
    heading: vehicle.heading,
    edgesLeft: Math.max(0, vehicle.route.length - vehicle.routeIndex),
    // Worked out here rather than sent. The client holds the county, so it has
    // every edge's length and limit already, and a second copy on the wire is a
    // second thing that can disagree with the line drawn on the map.
    metersLeft: left.meters,
    /**
     * Drive time at the speed this driver actually drives.
     *
     * `routeRemaining` quotes free-flow, and free-flow is a lie twice over for
     * a player: their multiplier is theirs, and with 2x on, the panel promised
     * double the real time. Speed scales time inversely, so the multiplier
     * divides.
     */
    secondsLeft: left.seconds / Math.max(0.2, vehicle.speedMultiplier),
  };
}

function soloModel(
  world: World,
  county: County,
  frame: Renderable[],
  input: InputState,
  fps: number,
): HudModel {
  const selected =
    input.selectedId === null
      ? null
      : (world.vehicles.find((v) => v.id === input.selectedId) ?? null);
  return {
    connection: 'solo',
    tick: world.tick,
    time: world.time,
    paused: input.paused,
    speed: SIM_SPEEDS[input.speedIndex] as number,
    fps,
    visible: frame.length,
    total: world.vehicles.length,
    // Solo is the client simulating the whole county, so it legitimately knows
    // every plate. Live mode does not, and that difference is not cosmetic.
    selected: selected
      ? describe(
          county,
          selected,
          selected.plate.text,
          `${selected.plate.jurisdiction} (${selected.plate.abbr})`,
        )
      : null,
    self: null,
  };
}

function liveModel(
  net: NetClient,
  county: County,
  frame: Renderable[],
  input: InputState,
  fps: number,
  /** The administrator's latest look at the selected car, or null. */
  adminTruth: InspectReplyMessage | null,
  /** What the car is heading for, so the destination can be named properly. */
  going: { jobDrop: number; jobDropName: string; fuel: { name: string; node: number } | null },
): HudModel {
  const self = net.selfVehicle;
  const selfInfo: VehicleInfo | null =
    self && net.plate
      ? {
          ...describe(
            county,
            self,
            net.plate.text,
            `${net.plate.jurisdiction} (${net.plate.abbr})`,
          ),
          /**
           * Where you are actually going.
           *
           * This was the road name of **the last edge in the route the client
           * happens to hold**, which is not the same thing as the last edge of
           * the route. The protocol says so directly: routeIndex counts edges
           * the client never received. So the name came off whichever edge the
           * message ended on, and reported "unnamed road" while the player was
           * driving further down a road that plainly had a name.
           *
           * The destination *node* is authoritative and always sent, so this
           * asks that instead.
           *
           * A destination has a better name available in almost every case: the
           * job you took names a business, the fuel button names a station, and
           * failing both, the nearest town places you. The road name is used
           * only when it is a road name.
           */
          destination:
            self.route.length > self.routeIndex
              ? destinationName(county, self.destination, going.jobDrop, going.jobDropName, going.fuel)
              : 'none',
        }
      : null;

  let selected: VehicleInfo | null = null;
  if (input.selectedId !== null && input.selectedId !== self?.id) {
    selected = describeOther(net, county, input.selectedId, frame);
    // The administrator sees the truth. On any selected car -- read or not, near
    // or far, behind a block or not -- the operator's view stands in for what a
    // person at the curb could make out: the real plate, the road it is on, the
    // speed it is doing. This is exactly the power the admin seat is: the county
    // does not get to be unread to the person who runs it.
    if (adminTruth && adminTruth.id === input.selectedId && adminTruth.plate) {
      const base = selected ?? {
        how: 'in-sight' as const,
        plate: adminTruth.plate,
        jurisdiction: '',
        road: adminTruth.road,
        roadClass: '',
        mph: adminTruth.mph,
        heading: frame.find((r) => r.id === input.selectedId)?.heading ?? 0,
        edgesLeft: 0,
        metersLeft: 0,
        secondsLeft: 0,
      };
      selected = {
        ...base,
        how: 'in-sight',
        plate: adminTruth.plate,
        road: adminTruth.road,
        mph: adminTruth.mph,
      };
    }
  }

  return {
    connection: net.state,
    pingMs: net.pingMs,
    tick: net.tick,
    time: net.time,
    paused: false,
    speed: 1,
    fps,
    visible: net.visibleCount,
    readable: net.readableCount,
    remembered: net.rememberedCount,
    selected,
    self: selfInfo,
  };
}

/**
 * What this client knows about a car it did not come in.
 *
 * Three cases, and the differences between them are the game rather than the
 * interface. You can see a car; that is a dot. You can read its plate from
 * close enough; that is a drive-by. You read it a while ago and are going on
 * memory; that is a record with an age on it, and it stops being true the
 * moment they turn a corner.
 *
 * None of the three ever includes where they are going.
 */
function describeOther(
  net: NetClient,
  county: County,
  id: number,
  frame: Renderable[],
): VehicleInfo | null {
  const item = frame.find((r) => r.id === id);
  const reading = net.reading(id);

  if (reading) {
    return {
      how: 'in-sight',
      plate: reading.p,
      jurisdiction: jurisdictionName(county, reading.j),
      road: reading.r >= 0 ? county.roadNames[reading.r] || 'unnamed road' : 'parked',
      roadClass: reading.c >= 0 ? className(reading.c) : '',
      mph: Math.round(reading.s * 2.23694),
      heading: item?.heading ?? 0,
      edgesLeft: 0,
      metersLeft: 0,
      secondsLeft: 0,
      expired: reading.x === true,
    };
  }

  const seen = net.recall(id);
  if (seen) {
    return {
      how: 'remembered',
      plate: seen.plate,
      jurisdiction: jurisdictionName(county, seen.abbr),
      road: seen.road >= 0 ? county.roadNames[seen.road] || 'unnamed road' : 'parked',
      roadClass: seen.cls >= 0 ? className(seen.cls) : '',
      mph: Math.round(seen.speed * 2.23694),
      heading: item?.heading ?? 0,
      edgesLeft: 0,
      metersLeft: 0,
      secondsLeft: 0,
      agoSeconds: Math.max(0, net.time - seen.at),
      expired: seen.expired === true,
    };
  }

  if (!item) return null;
  // Seen and not identified. Which is most of the traffic, most of the time.
  return {
    how: 'unread',
    plate: '',
    jurisdiction: '',
    road: 'not established',
    roadClass: '',
    mph: 0,
    heading: item.heading,
    edgesLeft: 0,
    metersLeft: 0,
    secondsLeft: 0,
  };
}

/** The full name behind a two-letter plate code. */
function jurisdictionName(county: County, abbr: string): string {
  if (abbr === county.state.abbr) return `${county.state.name} (${abbr})`;
  const format = OUT_OF_STATE.find((f) => f.abbr === abbr);
  return format ? `${format.jurisdiction} (${abbr})` : abbr;
}

/** A heading in world radians, as a compass word, for "last seen heading NE". */
function headingWord(radians?: number): string {
  if (radians === undefined) return 'somewhere';
  const deg = (((radians * 180) / Math.PI) % 360 + 360) % 360;
  // Screen-space world: +x east, +y south, so 0 is E and 90 is S.
  const dirs = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'];
  return `heading ${dirs[Math.round(deg / 45) % 8]}`;
}

// -- input -------------------------------------------------------------------

interface InputState {
  paused: boolean;
  speedIndex: number;
  selectedId: number | null;
  following: boolean;
  /** A player the administrator is riding along with, or null. */
  rideAlongId: number | null;
}

interface InputHandlers {
  drive: (node: number) => void;
  stop: () => void;
  pick: (px: number, py: number, radius?: number) => Renderable | null;
  selected?: (id: number) => void;
  pullOver?: (id: number) => void;
  /** Route around what you have found, or stop doing that. */
  toggleAvoid?: () => void;
  /** Somewhere to navigate to without handing the wheel over. */
  waypoint?: (node: number) => void;
  /** Take the waypoint off, which nothing else can do. */
  clearWaypoint?: () => void;
  /** Whether there is one to take off, so the menu can say so. */
  hasWaypoint?: () => boolean;
  /** Take the parked car nearest to you, which is not yours. */
  steal?: () => void;
  /**
   * Whether taking one is even possible from here.
   *
   * Deliberately only what the client can actually know: a civilian, stopped.
   * Whether there is a car within reach is the server's answer, because the
   * server is the only thing that knows which cars are parked, which are
   * somebody else's and which have a person in them. A button greyed out on a
   * guess is worse than a button that gives a real reason when pressed.
   */
  canSteal?: () => boolean;
  /** Drive by hand: how hard, and which compass direction to head. */
  steer?: (throttle: number, want: number | null) => void;
  /**
   * Do something to the nearest camera within reach.
   *
   * Four tiers, and the gradient is the design: obstruction is reversible and
   * cheap, destruction is a procurement decision and brings the camera back
   * somewhere you have not found. See INTERFERENCE_TIERS.
   */
  interfere?: (how: Interference) => void;
  /** Toggle the administrator's speed allowance. */
  run?: () => void;
  /** Put the terminal up or down. */
  toggleConsole?: () => void;
  /** Put it down if it is up. True when it did something. */
  closeConsole?: () => boolean;
  /** File your finds and pull the community map. */
  share?: () => void;
  /** Turn the shared network on or off. */
  network?: () => void;
  isSolo: boolean;
}

/**
 * The nearest camera this player knows about, to their own car.
 *
 * Client-side only, and it can only ever pick from what the player already
 * found, so it hands nobody a camera they have not seen. The server still
 * checks the distance before anything happens.
 */
function nearestKnown(
  known: KnownCameras,
  net: NetClient | null,
): { id: string; distance: number } | null {
  const me = net?.selfVehicle;
  if (!me) return null;
  let best: { id: string; distance: number } | null = null;
  for (const camera of known.all()) {
    const distance = Math.hypot(camera.x - me.x, camera.y - me.y);
    if (!best || distance < best.distance) best = { id: camera.id, distance };
  }
  return best;
}

function attachInput(
  canvas: HTMLCanvasElement,
  viewport: Viewport,
  county: County,
  nodeIndex: SpatialIndex,
  handlers: InputHandlers,
): InputState {
  const state: InputState = {
    paused: false,
    speedIndex: 0,
    selectedId: null,
    following: false,
    rideAlongId: null,
  };

  let dragging = false;
  let dragged = 0;
  let lastX = 0;
  let lastY = 0;

  /**
   * Fingers currently on the glass.
   *
   * Two of them means a pinch, and a pinch is the only way to zoom on a touch
   * screen: there is no wheel, and a game whose whole subject is *where the
   * cameras are* is unusable if the player cannot get close enough to see one.
   */
  const touches = new Map<number, { x: number; y: number }>();
  let pinchDistance = 0;
  /**
   * Whether the gesture in hand came from a finger.
   *
   * A finger is not a cursor. It covers about forty pixels rather than one, and
   * it never holds still: a deliberate tap drifts several pixels between down
   * and up. Both of those were being measured with mouse numbers, so on a phone
   * a tap on a car was usually read as a small drag and thrown away, and when
   * it was not, the eighteen-pixel pick radius wanted an accuracy no thumb has.
   */
  let touching = false;
  /** When the current press started, so a long press can be told from a tap. */
  let downAt = 0;
  let lastTapAt = -Infinity;
  let lastTapX = 0;
  let lastTapY = 0;
  /** Held so a second tap can cancel the waypoint the first was about to set. */
  let waypointTimer = 0;

  /**
   * The map's own right-click menu, replacing the browser's.
   *
   * The browser's menu is about the page -- reload, view source, save image --
   * and none of that is about a county. Replacing it is also the only place a
   * right click can reach "remove waypoint", which otherwise has nowhere to
   * live: a waypoint is set by tapping, and there is no tap that means unset.
   */
  const mapMenu = document.getElementById('map-menu');
  if (!mapMenu) throw new Error('missing element #map-menu');
  let menuAt = { x: 0, y: 0 };

  const closeMenu = (): void => {
    mapMenu.hidden = true;
  };

  canvas.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    const rect = canvas.getBoundingClientRect();
    menuAt = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    // "Remove" is shown even with nothing to remove, disabled, so the menu does
    // not change shape under the cursor between one right click and the next.
    const clear = mapMenu.querySelector<HTMLButtonElement>('[data-does="clear"]');
    if (clear) clear.disabled = !handlers.hasWaypoint?.();
    /**
     * Taking a car lives here rather than on a button of its own.
     *
     * There is already a menu for "things you can do at this spot", and a
     * tenth button along the bottom of the screen for something you can do
     * about twice a session is the wrong shape. Shown always and disabled when
     * there is nothing to take, for the same reason "remove waypoint" is: a
     * menu that changes shape under the cursor is a menu you misclick.
     */
    const steal = mapMenu.querySelector<HTMLButtonElement>('[data-does="steal"]');
    if (steal) steal.disabled = !handlers.canSteal?.();
    mapMenu.style.left = `${Math.min(event.clientX, window.innerWidth - 180)}px`;
    mapMenu.style.top = `${Math.min(event.clientY, window.innerHeight - 130)}px`;
    mapMenu.hidden = false;
  });

  mapMenu.addEventListener('click', (event) => {
    const does = (event.target as HTMLElement).dataset.does;
    closeMenu();
    if (!does) return;
    const ground = viewport.toWorld(menuAt.x, menuAt.y);
    const node = nearestRoadNode(county, nodeIndex, ground.x, ground.y);
    if (does === 'clear') handlers.clearWaypoint?.();
    else if (node >= 0 && does === 'waypoint') handlers.waypoint?.(node);
    else if (does === 'steal') handlers.steal?.();
    else if (node >= 0 && does === 'drive') handlers.drive(node);
  });

  window.addEventListener('pointerdown', (event: PointerEvent) => {
    if (!mapMenu.hidden && !mapMenu.contains(event.target as Node)) closeMenu();
  });

  canvas.addEventListener('pointerdown', (event) => {
    /**
     * Only the primary button.
     *
     * Nothing here looked at `button`, so a right click ran the whole gesture:
     * it set waypoints, it double-tapped into driving somewhere, and it did it
     * while the context menu was opening on top. `button` is 0 for the left
     * one and for every touch, which is exactly the set that should be doing
     * this.
     */
    if (event.button !== 0) return;
    touching = event.pointerType === 'touch' || event.pointerType === 'pen';
    downAt = performance.now();
    touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (touches.size === 2) {
      const [a, b] = [...touches.values()];
      pinchDistance = Math.hypot((a as { x: number }).x - (b as { x: number }).x,
        (a as { y: number }).y - (b as { y: number }).y);
      // A pinch is not a drag, and letting it be both makes the map lurch.
      dragging = false;
      canvas.classList.remove('dragging');
      return;
    }
    dragging = true;
    dragged = 0;
    lastX = event.clientX;
    lastY = event.clientY;
    // Capture can be refused (a synthetic event, a pointer already released).
    // Losing it costs nothing here, so it must not take the handler down.
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      /* not capturable */
    }
    canvas.classList.add('dragging');
  });

  canvas.addEventListener('pointermove', (event) => {
    if (touches.has(event.pointerId)) {
      touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }

    if (touches.size === 2) {
      const [a, b] = [...touches.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDistance > 0 && distance > 0) {
        const rect = canvas.getBoundingClientRect();
        viewport.zoomAt(
          (a.x + b.x) / 2 - rect.left,
          (a.y + b.y) / 2 - rect.top,
          distance / pinchDistance,
        );
        // A pinch is a deliberate look somewhere, so it stops the camera
        // chasing the player's own car.
        state.following = false;
      }
      pinchDistance = distance;
      return;
    }

    if (!dragging) return;
    const dx = event.clientX - lastX;
    const dy = event.clientY - lastY;
    lastX = event.clientX;
    lastY = event.clientY;
    dragged += Math.abs(dx) + Math.abs(dy);
    viewport.panByPixels(dx, dy);
    // Panning by hand means you want to look somewhere else, so it releases
    // the follow lock rather than fighting it.
    if (dragged > (touching ? 12 : 4)) state.following = false;
  });

  const endDrag = (event: PointerEvent): void => {
    if (event.button !== 0 && event.type === 'pointerup') return;
    touches.delete(event.pointerId);
    if (touches.size < 2) pinchDistance = 0;
    if (!dragging) return;
    dragging = false;
    canvas.classList.remove('dragging');
    try {
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
    // The slop a tap is allowed. A finger drifts; a mouse does not.
    if (dragged > (touching ? 12 : 4)) return;

    const rect = canvas.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;

    const hit = handlers.pick(px, py, touching ? 34 : 18);
    if (hit) {
      state.selectedId = hit.id;
      handlers.selected?.(hit.id);
      return;
    }

    if (handlers.isSolo) return;
    const ground = viewport.toWorld(px, py);
    const node = nearestRoadNode(county, nodeIndex, ground.x, ground.y);
    if (node < 0) return;

    /**
     * What a tap on the map means.
     *
     * A single tap **sets a waypoint**: it draws the way there and does not
     * touch the car. A **double tap** hands the wheel over and the server
     * drives you.
     *
     * The obvious problem with a double tap is that the first of the two taps
     * has already done something, so the pair does a thing and then undoes it.
     * The answer is not to pick a different gesture, it is to wait: a tap holds
     * for a fifth of a second to see whether a second one is coming, and only
     * commits the waypoint if none does. Two hundred milliseconds is under the
     * threshold where a delay reads as lag.
     *
     * Two problems, one answer. On a phone, a tap was doing double duty —
     * select the car under your thumb, or set off driving if you missed — so
     * missing a car by six pixels sent you across the county. And a driver
     * using WASD had no way to see where they were going, because the only
     * thing that drew a line also took the wheel away.
     *
     * Now the cheap gesture is the harmless one. Nothing that takes control
     * happens by accident, and navigating by hand is the default rather than
     * the thing you cannot do.
     */
    const now = performance.now();
    const second = now - lastTapAt < 320 && Math.hypot(px - lastTapX, py - lastTapY) < 34;
    lastTapAt = now;
    lastTapX = px;
    lastTapY = py;

    if (second) {
      // The waypoint the first tap was about to set never happens, and any
      // waypoint already on the map goes with it: being driven somewhere and
      // navigating there yourself are the same journey, and drawing both left
      // two lines on the map in two colors saying the same thing.
      window.clearTimeout(waypointTimer);
      handlers.drive(node);
      return;
    }
    window.clearTimeout(waypointTimer);
    waypointTimer = window.setTimeout(() => handlers.waypoint?.(node), 200);
  };
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  canvas.addEventListener(
    'wheel',
    (event) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      viewport.zoomAt(
        event.clientX - rect.left,
        event.clientY - rect.top,
        Math.exp(-event.deltaY * 0.0016),
      );
    },
    { passive: false },
  );

  /**
   * Driving by hand.
   *
   * W and S are the pedals. A and D are **not** west and east: they are left
   * and right relative to the way the car is pointing, because roads do not run
   * along the compass and a driver holding left means the left-hand road out of
   * whatever junction is ahead. Holding it early is signaling a turn.
   *
   * The car stays glued to the road either way. There is no steering *along* an
   * edge to get wrong, which is what makes this feel like driving rather than
   * like nudging a dot around: everything the simulated traffic gets --
   * acceleration, braking into a corner, corner speed by radius -- is the same
   * code path, and the only thing a driver adds is the choice at the junction.
   *
   * A used to toggle routing around cameras. That moved into the Cameras app,
   * which is where a decision you make sitting still belongs anyway.
   */
  /**
   * WASD are compass directions, which is what they are everywhere.
   *
   * W is north, S is south, A is west, D is east. Not left and right relative
   * to the car -- that was the first attempt and it is the wrong model here:
   * Waukesha is a section grid, and "keep going north" is a thing a driver
   * actually thinks where "take the second left" is not.
   *
   * The road supplies the geometry. Roads do not run along the compass, so at
   * each junction the car takes whichever road leaves closest to the direction
   * asked for, and follows it wherever it bends in between. There is nothing to
   * steer along an edge because there is nothing to steer: the car is on a road.
   *
   * The direction stands until it is changed, so crossing a county is one
   * keypress rather than a held finger and sixty messages a second. Space
   * stops, because S is south.
   */
  const NORTH = Math.PI / 2;
  const SOUTH = -Math.PI / 2;
  const EAST = 0;
  const WEST = Math.PI;

  function head(want: number): void {
    handlers.steer?.(1, want);
  }

  // The compass rose: WASD for a thumb. Each direction heads that way on the
  // roads; the middle stops. pointerdown rather than click so it answers a
  // finger as fast as a key does.
  const compassHeadings: Record<string, number> = {
    n: NORTH,
    s: SOUTH,
    e: EAST,
    w: WEST,
    ne: Math.PI / 4,
    nw: (3 * Math.PI) / 4,
    se: -Math.PI / 4,
    sw: (-3 * Math.PI) / 4,
  };
  document.getElementById('compass')?.addEventListener('pointerdown', (event) => {
    const dir = (event.target as HTMLElement).closest<HTMLElement>('[data-dir]')?.dataset.dir;
    if (!dir) return;
    event.preventDefault();
    if (dir === 'stop') {
      handlers.stop();
      handlers.steer?.(0, null);
      return;
    }
    const want = compassHeadings[dir];
    if (want !== undefined) head(want);
  });

  window.addEventListener('keydown', (event) => {
    if (event.repeat) return;
    /**
     * Nothing reaches the car while the gate is up.
     *
     * The overlay stops clicks, but keydown is on the window, so W and S and D
     * would have driven the guest car sitting behind the door -- which is the
     * exact "play without a passkey" the gate exists to forbid. If the landing
     * is showing, the keyboard does nothing.
     */
    const gate = document.getElementById('landing');
    if (gate && !gate.hidden) return;
    /**
     * Not while somebody is typing into a box.
     *
     * Every letter on this keyboard drives the car or does something to a
     * camera, and nothing checked where the keystroke was going. Typing a plate
     * into the registration field sent D and S and T to the road: the car
     * pulled away southeast, the 2x speed toggle came on and off, and the form
     * had not even been submitted yet. Any text field in this game was a
     * steering wheel.
     */
    const into = event.target as HTMLElement | null;
    if (
      into &&
      (into.tagName === 'INPUT' || into.tagName === 'TEXTAREA' || into.isContentEditable)
    ) {
      return;
    }
    switch (event.key) {
      case ' ':
        /**
         * The brake. S is south, so stopping needs its own key.
         *
         * There were two `case ' '` arms in this switch and the second one --
         * the one that lifted off the accelerator -- was unreachable. So the
         * space bar cleared the route and left the throttle where it was, which
         * on a car being driven by hand is not a brake at all.
         */
        event.preventDefault();
        if (handlers.isSolo) {
          state.paused = !state.paused;
        } else {
          handlers.stop();
          handlers.steer?.(0, null);
        }
        break;
      case '1':
      case '2':
      case '3':
        if (handlers.isSolo) state.speedIndex = Number(event.key) - 1;
        break;
      case 'f':
      case 'F':
        state.following = !state.following;
        break;
      case 'Escape':
        // Put the terminal down first if it is up; the road is underneath it.
        if (handlers.closeConsole?.()) break;
        state.selectedId = null;
        break;
      case 'w':
      case 'W':
        head(NORTH);
        break;
      case 's':
      case 'S':
        head(SOUTH);
        break;
      case 'a':
      case 'A':
        head(WEST);
        break;
      case 'd':
      case 'D':
        head(EAST);
        break;
      case 'q':
      case 'Q':
        handlers.toggleConsole?.();
        break;
      case 'x':
      case 'X':
        // Pull over whatever is selected. The server decides whether you were
        // actually close enough to do it.
        if (state.selectedId !== null) handlers.pullOver?.(state.selectedId);
        break;
      case 'c':
      case 'C':
        // Cover the nearest camera you can reach. The cheapest tier, and the
        // one the design wants people reaching for first.
        handlers.interfere?.('obstruct');
        break;
      case 'v':
      case 'V':
        // Break it. A service call, a day or two.
        handlers.interfere?.('damage');
        break;
      case 'b':
      case 'B':
        // Take it off the pole. It is worth something, and its replacement
        // lands somewhere you have not found.
        handlers.interfere?.('take');
        break;
      case 'r':
      case 'R':
        // The administrator's allowance. Ignored for everybody else.
        handlers.run?.();
        break;
      case 'm':
      case 'M':
        // Share what you found, and see what everybody else has.
        handlers.share?.();
        break;
      case 'n':
      case 'N':
        // The shared network. One boolean, and the county behind it.
        handlers.network?.();
        break;
    }
  });

  return state;
}

/**
 * The nearest named settlement to a point.
 *
 * A road name alone does not place anybody in a county twenty miles
 * across: "Reddelien Road" could be anywhere, and "Reddelien Road, Vernon" is
 * where you are. Nearest rather than containing, because the county's places
 * are points with populations rather than boundaries -- which is what the
 * archive gives, and inventing boundaries to get a tidier answer would be
 * inventing.
 */
function nearestPlace(county: County, x: number, y: number): string {
  return nearestPlaceWithin(county, x, y).name;
}

function nearestPlaceWithin(county: County, x: number, y: number): { name: string; away: number } {
  let best = '';
  let bestAway = Infinity;
  for (const place of county.places) {
    const away = Math.hypot(place.x - x, place.y - y);
    if (away < bestAway) {
      bestAway = away;
      best = place.name;
    }
  }
  return { name: best, away: bestAway };
}

/**
 * Whether a point is inside a ring, by the even-odd crossing rule.
 *
 * Rings are flat `[x, y, x, y, ...]` in county meters, closed implicitly: the
 * last point joins the first, which is why the modulo is there.
 */
function insideRing(ring: number[], x: number, y: number): boolean {
  let inside = false;
  const points = ring.length / 2;
  for (let i = 0; i < points; i++) {
    const j = (i + 1) % points;
    const x1 = ring[i * 2] as number;
    const y1 = ring[i * 2 + 1] as number;
    const x2 = ring[j * 2] as number;
    const y2 = ring[j * 2 + 1] as number;
    if (y1 > y !== y2 > y) {
      const crossing = x1 + ((y - y1) * (x2 - x1)) / (y2 - y1);
      if (crossing > x) inside = !inside;
    }
  }
  return inside;
}

/**
 * Where somebody is, said the way they would say it.
 *
 * "Near Waukesha" while standing in the middle of Waukesha is wrong in the way
 * that makes a panel feel like it is guessing -- so the county file carries the
 * actual municipal outlines and this asks which one a point is in.
 *
 * An estimate was tried first, deriving an extent from population on the usual
 * square-root relationship. It is fine for a dense city and off by about a
 * third for everywhere else here: New Berlin's 40,000 people are spread over
 * 36 square miles, so somebody two miles inside it was still told they
 * were near it. That is the exact case the line exists to get right, so the
 * estimate was not worth keeping.
 *
 * Outside every municipality -- which is most of a Wisconsin county by area --
 * the nearest named place is still the useful answer, and it says *near*.
 */
function placeAt(county: County, x: number, y: number): string {
  for (const muni of county.municipalities) {
    for (const ring of muni.rings) {
      if (insideRing(ring, x, y)) return `in ${muni.name}`;
    }
  }
  const here = nearestPlaceWithin(county, x, y);
  return here.name ? `near ${here.name}` : '';
}

/**
 * The best name available for where a car is headed.
 *
 * In order: the job you took, the pump you asked for, the road if it has a
 * name, and the nearest town if it does not.
 */
function destinationName(
  county: County,
  node: number,
  jobDrop: number,
  jobDropName: string,
  fuel: { name: string; node: number } | null,
): string {
  if (node >= 0 && node === jobDrop && jobDropName) return jobDropName;
  if (fuel && node >= 0 && node === fuel.node) return fuel.name;
  if (node < 0) return 'none';

  const x = county.nodeX[node] as number;
  const y = county.nodeY[node] as number;
  const town = nearestPlace(county, x, y);
  // Any road meeting that junction that has a name. A junction of two unnamed
  // lanes is a real thing and the town is the honest answer for it.
  const from = county.adjStart[node] as number;
  const to = county.adjStart[node + 1] as number;
  for (let e = from; e < to; e++) {
    const edge = county.adjEdge[e] as number;
    const name = edgeNameOf(county, edge);
    if (name) return town ? `${name}, ${town}` : name;
  }
  return town || 'somewhere with no name';
}

/** An edge's road name, or a plain admission that it has none. */
function roadNameOf(county: County, edge: number): string {
  return edgeNameOf(county, edge) || 'an unnamed road';
}

/**
 * The road, the way the sign by the road says it.
 *
 * A numbered highway is signed with a direction on it -- I 94 west, not I 94 --
 * and on a divided road that direction is a fact about the carriageway you are
 * on rather than about which way you happen to be pointing. Each carriageway is
 * its own one-way edge, so the edge knows.
 *
 * Only for the classes that carry a route number, and only when the edge is
 * one-way. A two-way state highway has no westbound side, and putting a
 * direction on Main Street would be inventing signage.
 */
function signedRoad(county: County, edge: number, heading: number): string {
  const name = roadNameOf(county, edge);
  const cls = className(county.edgeClass[edge] as number);
  if (!SIGNED_BY_NUMBER.has(cls)) return name;
  if (!county.edgeOneway[edge]) return name;
  return `${name} ${cardinal(heading)}`;
}

/** Classes signed with a route number, matching the bake's own list. */
const SIGNED_BY_NUMBER = new Set(['motorway', 'motorway_link', 'trunk', 'trunk_link']);

/**
 * One of the four a highway sign uses.
 *
 * Signs say east, west, north and south. They do not say south-east, so the
 * eight-point compass the heading row uses is the wrong vocabulary here even
 * though it is the more precise one.
 */
function cardinal(heading: number): string {
  const degrees = ((((-heading * 180) / Math.PI + 90) % 360) + 360) % 360;
  if (degrees < 45 || degrees >= 315) return 'north';
  if (degrees < 135) return 'east';
  if (degrees < 225) return 'south';
  return 'west';
}

/**
 * The road a stationary car is standing on.
 *
 * A stopped vehicle reports no edge, but it is still on the graph -- there is
 * nowhere else to be. The nearest junction's named roads are what it is parked
 * among, and the first named one is the answer a person would give.
 */
const parkedAt = new Map<string, string>();

function roadNear(county: County, x: number, y: number): string {
  // A parked car does not move, so this is asked repeatedly about one point.
  // Answered once per ten-meter square rather than scanning the county's
  // 44,704 junctions eight times a second to get the same answer back.
  const key = `${Math.round(x / 10)},${Math.round(y / 10)}`;
  const held = parkedAt.get(key);
  if (held !== undefined) return held;

  let bestNode = -1;
  let bestAway = Infinity;
  for (let n = 0; n < county.nodeCount; n++) {
    const away = Math.hypot((county.nodeX[n] as number) - x, (county.nodeY[n] as number) - y);
    if (away < bestAway) {
      bestAway = away;
      bestNode = n;
    }
  }
  let answer = 'an unnamed road';
  if (bestNode >= 0) {
    const from = county.adjStart[bestNode] as number;
    const to = county.adjStart[bestNode + 1] as number;
    for (let e = from; e < to; e++) {
      const name = edgeNameOf(county, county.adjEdge[e] as number);
      if (name) {
        answer = name;
        break;
      }
    }
  }
  // Bounded, because a long session parking in many places should not turn a
  // cache into a leak.
  if (parkedAt.size > 512) parkedAt.clear();
  parkedAt.set(key, answer);
  return answer;
}
